import { z } from 'zod';
import { LlmProvider } from '../enums';
import { LlmRegion, Thresholds } from '../scoring';
import { AssistantSettings } from './assistant';
import { ReferralSettings } from './referrals';

const cron = z
  .string()
  .trim()
  .regex(/^(\S+\s+){4}\S+$/, 'Expected a 5-field cron expression');

export const secretInput = z
  .string()
  .max(20_000)
  .nullable()
  .optional()
  .describe('Write-only. Omit to keep, null to clear.');

export const VeracrossSettings = z.object({
  schoolRoute: z.string().trim().min(1).max(64).regex(/^[a-z0-9_-]+$/i),
  clientId: z.string().trim().max(200),
  scopes: z.array(z.string().trim().min(1)).min(1),
  apiBaseUrl: z.string().url(),
  tokenUrlTemplate: z.string().url().or(z.string().includes('{school_route}')),
  syncSchedule: cron,
  profileUrlTemplate: z.string().max(500),
  admissionYear: z.number().int().min(2000).max(2100).nullable(),
});
export type VeracrossSettings = z.infer<typeof VeracrossSettings>;

export const VeracrossSettingsView = VeracrossSettings.extend({ clientSecretSet: z.boolean() });
export type VeracrossSettingsView = z.infer<typeof VeracrossSettingsView>;

export const UpdateVeracrossSettings = VeracrossSettings.partial().extend({ clientSecret: secretInput });
export type UpdateVeracrossSettings = z.infer<typeof UpdateVeracrossSettings>;

export const SftpSettings = z.object({
  host: z.string().trim().max(255),
  port: z.number().int().min(1).max(65535),
  username: z.string().trim().max(128),
  directory: z.string().trim().min(1).max(500),
  filePattern: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((v) => {
      try {
        new RegExp(v);
        return true;
      } catch {
        return false;
      }
    }, 'Invalid regular expression'),
  pollSchedule: cron,
  minFileAgeSeconds: z.number().int().min(0).max(86_400),
  deleteAfterProcessing: z.boolean(),
});
export type SftpSettings = z.infer<typeof SftpSettings>;

export const SftpSettingsView = SftpSettings.extend({
  passwordSet: z.boolean(),
  privateKeySet: z.boolean(),
});
export type SftpSettingsView = z.infer<typeof SftpSettingsView>;

export const UpdateSftpSettings = SftpSettings.partial().extend({
  password: secretInput,
  privateKey: secretInput,
});
export type UpdateSftpSettings = z.infer<typeof UpdateSftpSettings>;

export const ScoringSettings = z.object({
  provider: LlmProvider,
  model: z.string().trim().min(1).max(120),
  region: LlmRegion,
  maxConcurrency: z.number().int().min(1).max(16),
  maxNarrativeChars: z.number().int().min(500).max(50_000),
  scoreSchedule: cron,
  activePromptVersion: z.string().min(1),
  activeThresholdVersion: z.string().min(1),
});
export type ScoringSettings = z.infer<typeof ScoringSettings>;

export const UpdateScoringSettings = ScoringSettings.omit({
  activePromptVersion: true,
  activeThresholdVersion: true,
}).partial();
export type UpdateScoringSettings = z.infer<typeof UpdateScoringSettings>;

export const GeneralSettings = z.object({
  activeCycleId: z.string().uuid().nullable(),
});
export type GeneralSettings = z.infer<typeof GeneralSettings>;

export const AllSettingsView = z.object({
  general: GeneralSettings,
  veracross: VeracrossSettingsView,
  sftp: SftpSettingsView,
  scoring: ScoringSettings,
  assistant: AssistantSettings,
  referrals: ReferralSettings,
});
export type AllSettingsView = z.infer<typeof AllSettingsView>;

export const ConnectionTestResult = z.object({
  ok: z.boolean(),
  message: z.string(),
  status: z.number().int().nullable(),
  rawBody: z.string().nullable(),
  details: z.array(z.string()).optional(),
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResult>;

export const PromptVersionDto = z.object({
  id: z.string().uuid(),
  version: z.string(),
  template: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  active: z.boolean(),
});
export type PromptVersionDto = z.infer<typeof PromptVersionDto>;

export const CreatePromptVersionInput = z.object({
  template: z
    .string()
    .min(50)
    .max(50_000)
    .refine((t) => t.includes('{{input}}'), 'Template must contain the {{input}} placeholder'),
  notes: z.string().trim().max(1000).nullish(),
  activate: z.boolean().default(true),
});
export type CreatePromptVersionInput = z.infer<typeof CreatePromptVersionInput>;

export const ThresholdVersionDto = z.object({
  id: z.string().uuid(),
  version: z.string(),
  high: z.number().int(),
  medium: z.number().int(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  active: z.boolean(),
});
export type ThresholdVersionDto = z.infer<typeof ThresholdVersionDto>;

export const CreateThresholdVersionInput = z.object({
  thresholds: Thresholds,
  activate: z.boolean().default(true),
});
export type CreateThresholdVersionInput = z.infer<typeof CreateThresholdVersionInput>;

export const RescoreInput = z.object({
  scope: z.enum(['stale', 'all', 'failed']).default('stale'),
});
export type RescoreInput = z.infer<typeof RescoreInput>;

export const RescoreResult = z.object({ enqueued: z.number().int() });
export type RescoreResult = z.infer<typeof RescoreResult>;

export const CYCLE_CODE_PATTERN = /^SY(\d{2})-(\d{2})$/;

export function defaultCycleEndsOn(code: string): string {
  const m = CYCLE_CODE_PATTERN.exec(code);
  if (!m) throw new Error(`Invalid admission cycle code ${code}`);
  const first = Number(m[1]);
  const second = Number(m[2]);
  return `${2000 + second + (second < first ? 100 : 0)}-06-30`;
}

export const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v && d.getUTCFullYear() >= 2000 && d.getUTCFullYear() <= 2100;
  }, 'Expected a real date between 2000 and 2100');

export const CycleDto = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  active: z.boolean(),
  referralsOpen: z.boolean(),
  endsOn: z.string(),
});
export type CycleDto = z.infer<typeof CycleDto>;

export const CreateCycleInput = z.object({
  code: z.string().trim().regex(CYCLE_CODE_PATTERN, 'Expected e.g. SY26-27'),
  label: z.string().trim().min(1).max(100),
  activate: z.boolean().default(false),
});
export type CreateCycleInput = z.infer<typeof CreateCycleInput>;

export const UpdateCycleReferralsInput = z
  .object({
    referralsOpen: z.boolean().optional(),
    endsOn: CalendarDate.optional(),
  })
  .refine((v) => v.referralsOpen !== undefined || v.endsOn !== undefined, { message: 'Nothing to update' });
export type UpdateCycleReferralsInput = z.infer<typeof UpdateCycleReferralsInput>;
